import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePermission } from "@/auth/usePermission";
import {
  useV1InventoryMaterialRevisionsRetrieve,
  useV1InventoryMaterialRevisionsPartialUpdate,
  useV1InventoryUomList
} from "@/api/generated/v1/v1";
import { PageBanner } from "@/shared/components/PageBanner";

export function MaterialRevisionEditPage() {
  const navigate = useNavigate();
  const { familyId, id } = useParams<{ familyId: string, id: string }>();
  const canManage = usePermission("inventory.manage");

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [sku, setSku] = useState("");
  const [stockUomId, setStockUomId] = useState("");
  const [productionUomId, setProductionUomId] = useState("");
  const [baseNumerator, setBaseNumerator] = useState("");
  const [baseDenominator, setBaseDenominator] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { data: uomData } = useV1InventoryUomList(undefined, {
    query: {},
  });
  const uoms = uomData?.results ?? [];

  const { data: revision, isLoading: isRevisionLoading } = useV1InventoryMaterialRevisionsRetrieve(id!, {
    query: { enabled: !!id },
  });

  useEffect(() => {
    if (revision) {
      if (revision.status !== "draft") {
        navigate(`/inventory/materials/${familyId}`);
        return;
      }
      setName(revision.name || "");
      setDescription(revision.description || "");
      setSku(revision.sku || "");
      setStockUomId(revision.stock_uom || "");
      setProductionUomId(revision.production_uom || "");
      setBaseNumerator(revision.base_qty_numerator?.toString() || "");
      setBaseDenominator(revision.base_qty_denominator?.toString() || "");
    }
  }, [revision, familyId, navigate]);

  const { mutate, isPending } = useV1InventoryMaterialRevisionsPartialUpdate({
    mutation: {
      onSuccess: () => navigate(`/inventory/materials/${familyId}`),
      onError: (err: any) => {
        setError(
          err?.response?.data?.detail ||
          err?.response?.data?.name?.[0] ||
          "خطا در ویرایش ریویژن"
        );
      },
    },
  });

  if (!canManage) { navigate("/inventory/materials"); return null; }
  if (!familyId || !id) { navigate("/inventory/materials"); return null; }

  const handleSubmit = () => {
    setError(null);
    if (!name.trim()) { setError("نام متریال الزامی است"); return; }

    const payload: any = {
      name: name.trim(),
      description: description.trim(),
      sku: sku.trim(),
    };

    if (stockUomId) payload.stock_uom = stockUomId;
    if (productionUomId) payload.production_uom = productionUomId;
    if (baseNumerator) payload.base_qty_numerator = parseInt(baseNumerator);
    if (baseDenominator) payload.base_qty_denominator = baseDenominator;

    mutate({ id, data: payload });
  };

  const inputStyle = {
    width: "100%",
    padding: "0.6rem 0.9rem",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid var(--glass-border)",
    borderRadius: "8px",
    color: "var(--color-text)",
    fontSize: "1rem",
    boxSizing: "border-box" as const,
  };

  const labelStyle = {
    display: "block",
    marginBottom: "0.4rem",
    fontSize: "0.85rem",
    color: "var(--color-text-muted)",
  };

  if (isRevisionLoading) {
    return (
      <div className="p-8 flex-center">
        <p style={{ color: "var(--color-text-muted)" }}>در حال بارگذاری...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title="ویرایش ریویژن"
        subtitle={`اصلاح مشخصات ریویژن متریال`}
        icon="✏️"
        helpLink="/help/inventory/material-revision-edit"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <button
            onClick={() => navigate(`/inventory/materials/${familyId}`)}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            ← انصراف
          </button>
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ maxWidth: "600px", margin: "0 auto", padding: "2rem", width: "100%" }}>

        {error && (
          <div className="mb-4 p-3" style={{
            background: "rgba(255,0,0,0.1)", borderLeft: "4px solid red",
            borderRadius: "8px", color: "red",
          }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
          <div>
            <label style={labelStyle}>نام متریال <span style={{ color: "red" }}>*</span></label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="مثال: ورق فولادی A36" style={inputStyle} />
          </div>

          <div>
            <label style={labelStyle}>توضیحات</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="توضیحات اختیاری..." rows={2}
              style={{ ...inputStyle, resize: "vertical" }} />
          </div>

          <div>
            <label style={labelStyle}>کد فنی (SKU)</label>
            <input type="text" value={sku} onChange={(e) => setSku(e.target.value)}
              placeholder="کد انبار (اختیاری)" dir="ltr" style={inputStyle} />
          </div>

          <div style={{ borderTop: "1px solid var(--glass-border)", paddingTop: "1rem" }}>
            <p style={{ fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: "0.75rem" }}>
              واحدهای اندازه‌گیری
            </p>

            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: "140px" }}>
                <label style={labelStyle}>واحد انبار (ورود/خروج)</label>
                <select value={stockUomId} onChange={(e) => setStockUomId(e.target.value)} style={inputStyle}>
                  <option value="">انتخاب کنید...</option>
                  {uoms.map((u) => (
                    <option key={u.id} value={u.id}>{u.name} ({u.symbol || u.code})</option>
                  ))}
                </select>
              </div>

              <div style={{ flex: 1, minWidth: "140px" }}>
                <label style={labelStyle}>واحد تولید (BOM/مصرف)</label>
                <select value={productionUomId} onChange={(e) => setProductionUomId(e.target.value)} style={inputStyle}>
                  <option value="">انتخاب کنید...</option>
                  {uoms.map((u) => (
                    <option key={u.id} value={u.id}>{u.name} ({u.symbol || u.code})</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {stockUomId && productionUomId && stockUomId !== productionUomId && (
            <div style={{
              padding: "1rem",
              background: "rgba(255,255,255,0.03)",
              border: "1px solid var(--glass-border)",
              borderRadius: "8px",
            }}>
              <p style={{ fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: "0.75rem" }}>
                ضریب تبدیل پایه — فرمول: X واحد تولید = Y واحد انبار
              </p>
              <div style={{ display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: "100px" }}>
                  <label style={labelStyle}>
                    X (واحد {uoms.find(u => u.id === productionUomId)?.name ?? "تولید"})
                  </label>
                  <input type="number" value={baseNumerator}
                    onChange={(e) => setBaseNumerator(e.target.value)}
                    placeholder="مثال: 100" min="1" dir="ltr" style={inputStyle} />
                </div>
                <span style={{ color: "var(--color-text-muted)", paddingTop: "1.2rem" }}>=</span>
                <div style={{ flex: 1, minWidth: "100px" }}>
                  <label style={labelStyle}>
                    Y (واحد {uoms.find(u => u.id === stockUomId)?.name ?? "انبار"})
                  </label>
                  <input type="number" value={baseDenominator}
                    onChange={(e) => setBaseDenominator(e.target.value)}
                    placeholder="مثال: 3" min="0" step="0.0001" dir="ltr" style={inputStyle} />
                </div>
              </div>
              {baseNumerator && baseDenominator && (
                <p style={{ marginTop: "0.5rem", fontSize: "0.8rem", color: "var(--color-text-muted)" }}>
                  یعنی هر واحد {uoms.find(u => u.id === productionUomId)?.name} =&nbsp;
                  {(parseFloat(baseDenominator) / parseInt(baseNumerator)).toFixed(4)}&nbsp;
                  {uoms.find(u => u.id === stockUomId)?.name}
                </p>
              )}
            </div>
          )}

          <button className="btn btn-primary" onClick={handleSubmit} disabled={isPending}
            style={{ width: "100%", padding: "0.85rem", fontSize: "1rem" }}>
            {isPending ? "در حال ذخیره..." : "ذخیره تغییرات ✓"}
          </button>
        </div>
      </div>
    </div>
  );
}
